// Project status label maps (UX S6 / UI 11). The project header rendered raw
// enum values ("sales_order" / "in_production"); these turn them into the same
// Title Case the rest of the app uses. Kept project-scoped (not lib/orders.ts,
// which is order-status only and owned elsewhere) — same STAGE_ORDER progression
// as services/projects.ts.
//
// project_stage: opportunity · presentation · estimate · sales_order ·
//   in_production · shipped · invoiced · closed · lost
// project_state: open · on_hold · closed

const PROJECT_STAGE_LABELS: Record<string, string> = {
  opportunity: "Opportunity",
  presentation: "Presentation",
  // Work progress (CEO 2026-10-15): a quote being worked on, then an approved quote (never "order live" — Stephanie 2026-10-27).
  estimate: "Quoting",
  sales_order: "Approved",
  in_production: "In Production",
  shipped: "Shipped",
  invoiced: "Invoiced",
  closed: "Closed",
  lost: "Lost",
};

const PROJECT_STATE_LABELS: Record<string, string> = {
  open: "Open",
  on_hold: "On Hold",
  closed: "Closed",
};

/** Human label for a project statusStage (falls back to the raw value). */
export function projectStageLabel(stage: string): string {
  return PROJECT_STAGE_LABELS[stage] ?? stage;
}

/** Human label for a project statusState (falls back to the raw value). */
export function projectStateLabel(state: string): string {
  return PROJECT_STATE_LABELS[state] ?? state;
}

// ─── In-context create gates ───────────────────────────────────────────────
//
// The project-header Actions ▾ dropdown was removed 2026-07-27; its create
// actions each live in-context now (each tab + each document detail page). These
// predicates gate those "New presentation" / "New estimate" affordances to the
// SAME stages the backend rule engine offers them. The stage sets used to be
// hand-duplicated here (drift risk — proven in review), so they now come from
// @merchd/shared, which BOTH this frontend and the server getAvailableActions
// consume; the offline gate executes an equivalence check across all stages.
export {
  canCreatePresentationAtStage,
  canCreateEstimateAtStage,
} from "@merchd/shared";

/** The default project state — when a project sits here, the state pill is
 *  mechanical noise and collapses (only the stage pill shows). */
export const DEFAULT_PROJECT_STATE = "open";

/** Whether the statusState pill is meaningful enough to render alongside the
 *  stage pill. `open` is the default and collapses to a single pill. */
export function isMeaningfulProjectState(state: string): boolean {
  return state !== DEFAULT_PROJECT_STATE;
}

// ─── Derived project-stage progression ───────────────────────────────
//
// The server derives one node per stage (services/project-stages.ts) and the
// project-detail payload (`ProjectDetailResponse.stages`) carries them. The
// project tabs read node counts (`data.stages.find(s => s.key === …)?.count`)
// for their empty-state dead-end affordances — the cancellation-excluding
// truth the legacy `counts.*` fields don't give.
//
// These types formerly lived in the ProjectStageRail component; the visual
// stage rail that rendered them was removed 2026-07-26 (it duplicated the
// workspace sub-nav tabs). The derivation + payload + type stay — only the
// rail bar went — so this is their home now.

export type ProjectStageKey =
  | "presentation"
  | "estimate"
  | "sales_order"
  | "production"
  | "invoice";

export type ProjectStageStatus = "done" | "current" | "upcoming" | "skipped";

export interface ProjectStageAction {
  label: string;
  href: string;
}

export interface ProjectStageNode {
  key: ProjectStageKey;
  status: ProjectStageStatus;
  count: number;
  href: string;
  action?: ProjectStageAction;
}
